/**
 * "Where the reading went" — the page tree as a sunburst you can read and
 * drill into.
 *
 * Drawn here rather than with recharts' SunburstChart, which writes each arc's
 * VALUE on it and never its name, and has no zoom: the result was rings of bare
 * numbers and a tooltip reading ": 241 events". Here every arc carries its page
 * name where it fits, the centre explains whichever arc is under the pointer,
 * and a click on a section re-roots the chart there.
 *
 * Two rings at a time — the focused section's children and theirs. Deeper
 * levels are reached by drilling, not by shrinking a third ring to slivers.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { DataTable, type ColumnDef, type SortState } from '@shared-ui/components';
import type { SitePathNode } from '../../../api/siteAnalyticsApi';

/** A row of the table under the chart: one child of the focused section. */
interface Row { node: SitePathNode; path: string[]; colour: string }

/** Ring colours, cycled by the focused level's sections so each keeps one hue. */
const SECTION_COLOURS = ['#6366f1', '#8b5cf6', '#a855f7', '#c026d3', '#db2777', '#e11d48', '#ea580c', '#ca8a04'];

const SIZE = 480;
const C = SIZE / 2;
const RINGS = [{ r0: 78, r1: 150 }, { r0: 154, r1: 232 }] as const;
/** Below this sweep an arc gets no label — it would be unreadable. */
const LABEL_MIN_DEG = 7;

interface Arc {
  node: SitePathNode;
  /** Full URL path, `/chemistry-lab/acids`. */
  path: string[];
  ring: 0 | 1;
  a0: number;
  a1: number;
  fill: string;
}

const pt = (r: number, a: number) => [C + r * Math.sin(a), C - r * Math.cos(a)] as const;

/** An annular sector, clockwise from the top. A full turn is drawn as a hair
 *  short of one, because an arc whose ends coincide draws nothing. */
function sector(r0: number, r1: number, a0: number, a1: number): string {
  const end = Math.min(a1, a0 + Math.PI * 2 - 1e-4);
  const large = end - a0 > Math.PI ? 1 : 0;
  const [x0, y0] = pt(r1, a0);
  const [x1, y1] = pt(r1, end);
  const [x2, y2] = pt(r0, end);
  const [x3, y3] = pt(r0, a0);
  return `M${x0},${y0} A${r1},${r1} 0 ${large} 1 ${x1},${y1} L${x2},${y2} A${r0},${r0} 0 ${large} 0 ${x3},${y3} Z`;
}

function layout(focus: SitePathNode, base: string[]): Arc[] {
  const arcs: Arc[] = [];
  const total = Math.max(1, focus.events);
  let a = 0;
  focus.children.forEach((child, i) => {
    const sweep = (child.events / total) * Math.PI * 2;
    const fill = SECTION_COLOURS[i % SECTION_COLOURS.length];
    arcs.push({ node: child, path: [...base, child.name], ring: 0, a0: a, a1: a + sweep, fill });
    let b = a;
    for (const g of child.children) {
      const gs = (g.events / total) * Math.PI * 2;
      arcs.push({ node: g, path: [...base, child.name, g.name], ring: 1, a0: b, a1: b + gs, fill });
      b += gs;
    }
    a += sweep;
  });
  return arcs;
}

const urlOf = (path: string[]) => (path.length === 0 ? 'All pages' : path[0] === '/' ? '/' : `/${path.join('/')}`);

/**
 * What the chart is of — its words, not its drawing. The default is the page
 * tree ("Where the reading went"); another tree says what its slices are, what
 * sizes them (`events`, which must add up the rings) and what the second
 * number is (`visits`).
 */
export interface SunburstVocab {
  /** A node's name from its path; the empty path is the whole chart. */
  nameOf: (path: string[]) => string;
  /** The first column's heading at the top level. */
  sectionHeader: string;
  /** What children are called in the table ("3 pages ›"). */
  childNoun: string;
  /** What sizes a slice: a column heading and, in the centre, a unit. */
  sizeLabel: string;
  sizeUnit: string;
  /** The second number — a column heading, and its name in the detail. */
  countLabel: string;
  countDetail: string;
  /** The explanation shown when nothing is hovered. */
  hint: (where: string) => ReactNode;
}

const PAGE_VOCAB: SunburstVocab = {
  nameOf: urlOf,
  sectionHeader: 'Section',
  childNoun: 'pages',
  sizeLabel: 'Page views',
  sizeUnit: 'page views',
  countLabel: 'Sessions',
  countDetail: 'Visits',
  hint: where => (
    <>
      Inner ring: the sections of {where}. Outer ring: the pages inside each one — a gap beside them is
      the section’s own page. Each slice is sized by its share of page views. Hover a slice for its
      numbers; click one with pages under it to open it.
    </>
  ),
};
const pct = (n: number, of: number) => `${((n / Math.max(1, of)) * 100).toFixed(1)}%`;

/** Label along the radius, flipped on the left half so it never reads upside down. */
function ArcLabel({ arc }: { arc: Arc }) {
  const sweepDeg = ((arc.a1 - arc.a0) * 180) / Math.PI;
  if (sweepDeg < LABEL_MIN_DEG) return null;
  const { r0, r1 } = RINGS[arc.ring];
  const mid = (arc.a0 + arc.a1) / 2;
  const [x, y] = pt((r0 + r1) / 2, mid);
  const deg = (mid * 180) / Math.PI;
  const rotate = deg <= 180 ? deg - 90 : deg + 90;
  const maxChars = Math.floor((r1 - r0 - 8) / 6.2);
  const name = arc.node.name.length > maxChars ? `${arc.node.name.slice(0, maxChars - 1)}…` : arc.node.name;
  return (
    <text x={x} y={y} transform={`rotate(${rotate} ${x} ${y})`} textAnchor="middle" dominantBaseline="central"
      className="ps-label">{name}</text>
  );
}

export default function PathSunburst({ root, vocab = PAGE_VOCAB }: { root: SitePathNode; vocab?: SunburstVocab }) {
  const nameOf = vocab.nameOf;
  const [focusPath, setFocusPath] = useState<string[]>([]);
  const [hover, setHover] = useState<Arc | null>(null);

  // The focused node, found by walking the path. A path that no longer exists
  // (the window changed under it) falls back to the root rather than blanking.
  const { focus, base } = useMemo(() => {
    let n: SitePathNode | undefined = root;
    for (const part of focusPath) n = n?.children.find(c => c.name === part);
    return n ? { focus: n, base: focusPath } : { focus: root, base: [] as string[] };
  }, [root, focusPath]);
  const arcs = useMemo(() => layout(focus, base), [focus, base]);

  const [sort, setSort] = useState<SortState>({ sortBy: 'events', sortOrder: 'desc' });
  const [query, setQuery] = useState('');
  const rows = useMemo<Row[]>(() => {
    const q = query.trim().toLowerCase();
    return focus.children
      .map((node, i) => ({ node, path: [...base, node.name], colour: SECTION_COLOURS[i % SECTION_COLOURS.length] }))
      .filter(r => !q || nameOf(r.path).toLowerCase().includes(q));
  }, [focus, base, query, nameOf]);
  const columns = useMemo<ColumnDef<Row>[]>(() => [
    {
      key: 'path', header: base.length ? `Inside ${nameOf(base)}` : vocab.sectionHeader, sortable: true,
      sortValue: r => nameOf(r.path).toLowerCase(),
      render: r => (
        <span className="ps-cell">
          <span className="ps-swatch" style={{ background: r.colour }} />
          {nameOf(r.path)}
          {r.node.children.length > 0 && <span className="ps-count">{r.node.children.length} {vocab.childNoun} ›</span>}
        </span>
      ),
    },
    {
      key: 'events', header: vocab.sizeLabel, sortable: true, className: 'sa-num',
      sortValue: r => r.node.events, render: r => r.node.events.toLocaleString(),
    },
    {
      key: 'visits', header: vocab.countLabel, sortable: true, className: 'sa-num',
      sortValue: r => r.node.visits, render: r => r.node.visits.toLocaleString(),
    },
    {
      key: 'share', header: 'Share', sortable: true, className: 'sa-num',
      sortValue: r => r.node.events, render: r => pct(r.node.events, focus.events),
    },
  ], [base, focus, nameOf, vocab]);

  const drill = (arc: Arc) => {
    if (arc.node.children.length) { setFocusPath(arc.path); setHover(null); }
  };

  return (
    <div className="ps">
      <nav className="ps-crumbs" aria-label="Section">
        <button onClick={() => { setFocusPath([]); setHover(null); }} disabled={base.length === 0}>{nameOf([])}</button>
        {base.map((part, i) => (
          <span key={i}>
            <span className="ps-sep">›</span>
            <button onClick={() => { setFocusPath(base.slice(0, i + 1)); setHover(null); }}
              disabled={i === base.length - 1}>{part}</button>
          </span>
        ))}
      </nav>

      <div className="ps-body">
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="ps-svg" role="img"
          aria-label={`${vocab.sizeLabel} under ${nameOf(base)}`}>
          {arcs.map(arc => {
            const { r0, r1 } = RINGS[arc.ring];
            const canDrill = arc.node.children.length > 0;
            return (
              <path key={arc.path.join('/')} d={sector(r0, r1, arc.a0, arc.a1)} fill={arc.fill}
                className={`ps-arc ring-${arc.ring}${canDrill ? ' can-drill' : ''}${hover === arc ? ' is-hover' : ''}`}
                tabIndex={0}
                aria-label={`${nameOf(arc.path)}: ${arc.node.events} ${vocab.sizeUnit}`}
                onMouseEnter={() => setHover(arc)} onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(arc)} onBlur={() => setHover(null)}
                onClick={() => drill(arc)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); drill(arc); } }} />
            );
          })}
          {arcs.map(arc => <ArcLabel key={`l-${arc.path.join('/')}`} arc={arc} />)}

          {/* The centre says what the pointer is on — or, with nothing under
              it, what the whole chart is. Clicking it goes up a level. */}
          <g className={base.length ? 'ps-centre can-up' : 'ps-centre'}
            onClick={() => { if (base.length) { setFocusPath(base.slice(0, -1)); setHover(null); } }}>
            <circle cx={C} cy={C} r={RINGS[0].r0 - 4} />
            <text x={C} y={C - 16} textAnchor="middle" className="ps-centre-n">
              {(hover ? hover.node.events : focus.events).toLocaleString()}
            </text>
            <text x={C} y={C + 4} textAnchor="middle" className="ps-centre-unit">{vocab.sizeUnit}</text>
            <text x={C} y={C + 22} textAnchor="middle" className="ps-centre-sub">
              {hover ? `${pct(hover.node.events, focus.events)} of ${base.length ? 'this section' : 'all'}`
                : base.length ? '↑ click to go up' : 'hover a slice'}
            </text>
          </g>
        </svg>

        <div className="ps-detail" aria-live="polite">
          {hover ? (
            <>
              <div className="ps-detail-path">{nameOf(hover.path)}</div>
              <dl>
                <dt>{vocab.sizeLabel}</dt><dd>{hover.node.events.toLocaleString()}</dd>
                <dt>{vocab.countDetail}</dt><dd>{hover.node.visits.toLocaleString()}</dd>
                <dt>Share</dt><dd>{pct(hover.node.events, focus.events)} of {base.length ? nameOf(base) : `all ${vocab.sizeUnit}`}</dd>
                {hover.node.children.length > 0 && <><dt>Under it</dt><dd>{hover.node.children.length} {vocab.childNoun}</dd></>}
              </dl>
              {hover.node.children.length > 0 && <p className="ps-hint">Click to open this section.</p>}
            </>
          ) : (
            <p className="ps-hint">{vocab.hint(base.length ? nameOf(base) : 'the site')}</p>
          )}
        </div>
      </div>

      <DataTable<Row>
        columns={columns}
        data={rows}
        rowKey={r => r.path.join('/')}
        sort={sort}
        onSortChange={setSort}
        onSearch={setQuery}
        searchPlaceholder={base.length ? `Filter ${vocab.childNoun} in ${nameOf(base)}…` : `Filter ${vocab.sectionHeader.toLowerCase()}…`}
        onRowClick={r => { if (r.node.children.length) { setFocusPath(r.path); setHover(null); } }}
        rowClickable={r => r.node.children.length > 0}
        emptyMessage={query ? `Nothing here matches “${query}”.` : `No ${vocab.childNoun} here.`}
        compact
      />
    </div>
  );
}
